// The app's only door to the server. Every failure becomes words a person can
// act on; nothing is retried silently forever.

import { DEMO, DEMO_REFUSAL } from "../demo/mode";
import { readStored, removeStored, writeStored } from "./stored";

const TOKEN_KEY = "cb:token";
export const BILLING_REQUIRED_EVENT = "cb:billing-required";
function billingRefused(status: number, code?: string) {
  if (status === 402 && (code === "billing_required" || code === "trial_required") && typeof window !== "undefined") window.dispatchEvent(new Event(BILLING_REQUIRED_EVENT));
}

export function getToken(): string | null {
  return readStored(TOKEN_KEY);
}

export function setToken(t: string | null) {
  // private mode: the session lasts as long as the tab
  if (t) writeStored(TOKEN_KEY, t);
  else removeStored(TOKEN_KEY);
  // signing in or out leaves nothing of an earlier sign-in in this tab: no new email's
  // one-use ticket, no code on its way (the keys Welcome keeps them under)
  try {
    sessionStorage.removeItem("cb:ticket");
    sessionStorage.removeItem("cb:signin");
  } catch {
    /* private mode: nothing was kept */
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
  ) {
    super(message);
  }
}

export async function api<T = Record<string, unknown>>(path: string, opts: { method?: string; body?: unknown; token?: string | null; keepalive?: boolean } = {}): Promise<T> {
  // the demo (/demo) never reaches the server: it answers from its sample, and refuses what needs an account
  if (DEMO) return (await import("../demo/api")).demoApi(path, opts) as Promise<T>;
  const token = opts.token === undefined ? getToken() : opts.token;
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
      headers: {
        ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      keepalive: opts.keepalive,
    });
  } catch {
    throw new ApiError(0, "Could not reach the board. Check your connection.", "network");
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; code?: string };
    billingRefused(res.status, d.code);
    throw new ApiError(res.status, d.error ?? `The board answered ${res.status}.`, d.code ?? "error");
  }
  return data as T;
}

/** Sends a file's bytes as they are, for a picture: the same door, the same words when it fails. */
export async function apiUpload<T = Record<string, unknown>>(path: string, body: Blob, as?: string | null): Promise<T> {
  if (DEMO) throw new ApiError(403, DEMO_REFUSAL, "demo");
  const token = as === undefined ? getToken() : as;
  let res: Response;
  try {
    res = await fetch(path, { method: "POST", headers: { "Content-Type": body.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body });
  } catch {
    throw new ApiError(0, "Could not reach the board. Check your connection.", "network");
  }
  const data = (await res.json().catch(() => null)) as (T & { error?: string; code?: string }) | null;
  if (!res.ok) {
    billingRefused(res.status, data?.code);
    throw new ApiError(res.status, data?.error ?? `The board answered ${res.status}.`, data?.code ?? "error");
  }
  return data as T;
}
